import React, { useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import AtomApp from './components/AtomApp';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import { FlexStudio } from './components/FlexStudio';
import LlmPlayground from './components/LlmPlayground';
import RegistryManagerPage from './components/RegistryManagerPage';
import TaskMonitorPage from './components/TaskMonitorPage';
import { FastStudioShell } from './components/FastStudio';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { createMuiTheme } from './theme/muiTheme';

/**
 * Protected route wrapper - redirects to login if not authenticated
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { colors } = useTheme();

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.background,
        }}
      >
        <CircularProgress sx={{ color: colors.success.main }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * Standalone sign-up route (/sign_up) -- lets the registration page be linked
 * to and bookmarked directly, instead of only being reachable via the
 * in-app "Sign Up" toggle inside AppContent. Already-authenticated users are
 * redirected away; on success/"back to login" it navigates to "/" (root
 * shows the login screen when unauthenticated).
 */
function SignUpRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.background,
        }}
      >
        <CircularProgress sx={{ color: colors.success.main }} />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <RegisterPage
      colors={colors}
      onNavigateToLogin={() => navigate('/')}
    />
  );
}

/**
 * Main app content with authentication handling
 */
function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { colors, themeMode, toggleTheme } = useTheme();
  const [authPage, setAuthPage] = useState('login');

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.background,
        }}
      >
        <CircularProgress sx={{ color: colors.success.main }} />
      </Box>
    );
  }

  // Show login or register page if not authenticated
  if (!isAuthenticated) {
    if (authPage === 'register') {
      return (
        <RegisterPage
          colors={colors}
          onNavigateToLogin={() => setAuthPage('login')}
        />
      );
    }
    return (
      <LoginPage
        colors={colors}
        onNavigateToRegister={() => setAuthPage('register')}
      />
    );
  }

  // Show main app if authenticated (pass legacy props for gradual migration)
  return <AtomApp themeMode={themeMode} toggleTheme={toggleTheme} />;
}

/**
 * Theme-aware MUI provider wrapper
 * Uses our custom theme system to create MUI theme
 */
function ThemedMuiProvider({ children }) {
  const { themeId } = useTheme();

  // Memoize MUI theme creation
  const muiTheme = useMemo(() => createMuiTheme(themeId), [themeId]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

/**
 * App routes with theme provider
 */
function AppRoutes() {
  const { colors, themeId } = useTheme();

  return (
    <>
    <Routes>
      {/* NEW: FAST Studio Shell - Unified Hub (Phase 2 rewrite) */}
      <Route
        path="/studio"
        element={
          <ProtectedRoute>
            <FastStudioShell />
          </ProtectedRoute>
        }
      />

      {/* LEGACY: FLEX IDE - standalone IDE route (will be replaced by /studio) */}
      <Route
        path="/flex-ide"
        element={
          <ProtectedRoute>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
              <FlexStudio themeMode={themeId} />
            </Box>
          </ProtectedRoute>
        }
      />


      {/* Registry Manager - standalone route */}
      <Route
        path="/registry-manager"
        element={
          <ProtectedRoute>
            <RegistryManagerPage />
          </ProtectedRoute>
        }
      />

      {/* Task Monitor - standalone route */}
      <Route
        path="/task-monitor"
        element={
          <ProtectedRoute>
            <TaskMonitorPage />
          </ProtectedRoute>
        }
      />

      {/* Email Verification - public route (no auth required) */}
      <Route
        path="/verify-email"
        element={<EmailVerificationPage colors={colors} />}
      />

      {/* Password Reset - public route (no auth required) */}
      <Route
        path="/reset-password"
        element={<ResetPasswordPage colors={colors} />}
      />

      {/* Sign Up - standalone, directly linkable route (no auth required) */}
      <Route
        path="/sign_up"
        element={<SignUpRoute />}
      />

      {/* Main app routes */}
      <Route
        path="/*"
        element={<AppContent />}
      />
    </Routes>
    </>
  );
}

/**
 * Root App component
 *
 * Provider hierarchy:
 * 1. BrowserRouter - routing
 * 2. AuthProvider - authentication state
 * 3. ThemeProvider - custom theme system (4 themes)
 * 4. ThemedMuiProvider - MUI theme integration
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <ThemedMuiProvider>
            <AppRoutes />
          </ThemedMuiProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
